import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Skeleton } from '@shared/ui/feedback/Skeleton';
import { WidgetHeader } from './WidgetCard';
import { widgetTitle, type WidgetProps } from './registry';
import { useScopedUtilityLinks, type ScopedUtilityLink } from '@features/utilities/useScopedUtilityLinks';
import { useUsageActionMeta } from '@features/utilities/useUtilityLinkActions';
import { useUtilityLinkUsageFromMeta } from '@features/utilities/useUtilityLinkUsage';
import { getUtilityPresentation, utilityIconBg } from '@features/utilities/utilityPresentation';
import {
  bucketDailyDeltas,
  formatTrendPct,
  formatUsageTotal,
  splitWeekTotals,
  weekOverWeekDeltaPct,
} from '@features/utilities/utilityUsageMath';
import { UtilityUsageChart } from '@features/utilities/UtilityUsageChart';

/**
 * Home widget: one card per scoped utility link (active property + approved
 * claims). Timeframes driven by each link's get_usage parameters_schema.
 */
export function UtilitiesWidget({ config }: WidgetProps): JSX.Element | null {
  const navigate = useNavigate();
  const { scopedLinks, isLoading, isEmpty } = useScopedUtilityLinks();

  if (!isLoading && isEmpty) {
    return null;
  }

  return (
    <section>
      <WidgetHeader title={widgetTitle(config)} />
      <div className="-mx-5 flex gap-3 overflow-x-auto px-5 pb-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
        {isLoading ? (
          <>
            <Skeleton className="h-[9.5rem] w-64 shrink-0 rounded-2xl" />
            <Skeleton className="h-[9.5rem] w-64 shrink-0 rounded-2xl" />
          </>
        ) : (
          scopedLinks.map((link) => (
            <UtilityMeterCard
              key={link.link_id}
              link={link}
              onClick={() => navigate(`/app/utilities/${link.link_id}`)}
            />
          ))
        )}
      </div>
    </section>
  );
}

function usageReferenceMs(iso?: string | null): number {
  if (!iso) return Date.now();
  const ms = new Date(iso).getTime();
  return Number.isFinite(ms) ? ms : Date.now();
}

function UtilityMeterCard({
  link,
  onClick,
}: {
  link: ScopedUtilityLink;
  onClick: () => void;
}): JSX.Element | null {
  const { meta, isMetaReady } = useUsageActionMeta(link.link_id);
  // Visual identity comes from the handler-advertised kind/overrides (dynamic),
  // falling back to the name heuristic while meta loads.
  const presentation = getUtilityPresentation(link.utilityName, {
    utilityKind: meta?.utilityKind,
    chartKind: meta?.chartKind,
    color: meta?.presentationColor,
  });
  const Icon = presentation.icon;
  const { usagePrimary, usageComparison, isLoading } = useUtilityLinkUsageFromMeta(link.link_id, meta, {
    enabled: isMetaReady,
  });

  const { total, deltaPct, chartValues, chartLabels, summaryLabel } = useMemo(() => {
    const seriesPrimary = usagePrimary.data?.series ?? [];
    const seriesComparison = usageComparison.data?.series ?? [];
    const register =
      usagePrimary.data?.primary_register ??
      meta?.primaryRegisterCode ??
      usagePrimary.data?.registers[0];
    // Home widget is the handler-instructed "simple" display → force current week (7d).
    const dayCount = meta?.homeWidget.simple ? 7 : (meta?.homeWidget.chartDayCount ?? 7);
    const referenceMs = usageReferenceMs(usagePrimary.data?.to ?? usageComparison.data?.to);
    const buckets = bucketDailyDeltas(seriesPrimary, register, dayCount, referenceMs);
    const chartVals = buckets.map((b) => b.value);
    // Keep the headline total, chart, and trend consistent: the current-week
    // figure is the sum of the same daily buckets the chart renders, and the
    // trend compares it to the prior week from the comparison series.
    const currentWeek = chartVals.reduce((sum, v) => sum + v, 0);
    const { priorWeek } = splitWeekTotals(seriesComparison, register, referenceMs);
    const totalValue = usagePrimary.data?.total_consumption ?? currentWeek;
    return {
      total: formatUsageTotal(totalValue, usagePrimary.data?.unit),
      deltaPct: weekOverWeekDeltaPct(currentWeek, priorWeek),
      chartValues: chartVals,
      chartLabels: buckets.map((b) => b.label),
      summaryLabel: meta?.homeWidget.summaryLabel ?? 'This week',
    };
  }, [usagePrimary.data, usageComparison.data, meta]);

  if (isMetaReady && !meta) {
    return null;
  }

  const trendDown = deltaPct !== null && deltaPct < 0;
  const trendNeutral = deltaPct === null;
  const showLoading = !isMetaReady || isLoading;

  return (
    <button
      type="button"
      onClick={onClick}
      className="flex w-64 shrink-0 flex-col rounded-2xl bg-card p-4 text-left shadow-sm hairline active:scale-[0.99]"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-2">
          <span
            className="flex h-8 w-8 items-center justify-center rounded-pill"
            style={{ backgroundColor: utilityIconBg(presentation.color) }}
          >
            <Icon className="h-4.5 w-4.5" strokeWidth={1.75} style={{ color: presentation.color }} />
          </span>
          <div>
            <p className="text-body-sm font-semibold text-ink">{link.utilityName}</p>
            <p className="text-caption text-ink-muted">{summaryLabel}</p>
          </div>
        </div>
        <div className="text-right">
          {showLoading ? (
            <Skeleton className="ml-auto h-5 w-16" />
          ) : (
            <>
              <p className="font-bold text-ink">
                {total.amount}
                {total.unit ? (
                  <span className="text-caption font-medium text-ink-muted"> {total.unit}</span>
                ) : null}
              </p>
              <p
                className="text-caption font-medium"
                style={{
                  color: trendNeutral
                    ? 'var(--ink-muted)'
                    : trendDown
                      ? 'var(--success)'
                      : 'var(--danger)',
                }}
              >
                {formatTrendPct(deltaPct)}
              </p>
            </>
          )}
        </div>
      </div>

      <div className="mt-3">
        {showLoading ? (
          <Skeleton className="h-16 w-full" />
        ) : (
          <UtilityUsageChart
            data={chartValues}
            labels={chartLabels}
            variant={presentation.chartKind}
            color={presentation.color}
            size="compact"
            emptyMessage={`No usage for ${summaryLabel.toLowerCase()}`}
          />
        )}
      </div>
    </button>
  );
}
